import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  FormControlLabel,
  Checkbox,
  Collapse,
  Button,
  Stack,
  TextField,
  MenuItem,
} from '@mui/material';
import { DesktopTimePicker } from '@mui/x-date-pickers';
import { Cancel, Edit } from '@mui/icons-material';
import { nanoid } from 'nanoid';
import { generateScheduleBitString, generateAllOnesBitString, roundToNearestInterval, formatDateToISO } from '../../../time/timeUtils';
import { BitStringData, RecurringBreak } from '../../../types';
import ConfirmationDialog from '../../global/ConfirmationDialog';
import { TIME_INTERVAL, DAYJS_DAYS_OF_WEEK } from '../../../constants';
import { dayjs, Dayjs } from '../../../utils/dayjs-config';

type DayKey = typeof DAYJS_DAYS_OF_WEEK[number];

interface ScheduleCardProps {
  day: DayKey;
  initialWorkHours: BitStringData;
  onScheduleChange: (day: DayKey, updatedSchedule: BitStringData) => void;
  recurringBreaks: RecurringBreak[];
  onRecurringBreaksChange: (day: DayKey, breaks: RecurringBreak[]) => void;
  templateEndDate: Dayjs;
}

const toDayjsFromHHmm = (time: string): Dayjs => {
  const hours = parseInt(time.slice(0, 2), 10);
  const minutes = parseInt(time.slice(2), 10);
  return dayjs().hour(hours).minute(minutes).second(0).millisecond(0);
};

const ScheduleCard: React.FC<ScheduleCardProps> = ({
  day,
  initialWorkHours,
  onScheduleChange,
  recurringBreaks,
  onRecurringBreaksChange,
  templateEndDate,
}) => {
  const weekdayIndex = Math.max(0, DAYJS_DAYS_OF_WEEK.indexOf(day));
  const [workHours, setWorkHours] = useState<BitStringData>(initialWorkHours);
  const [localBreaks, setLocalBreaks] = useState<RecurringBreak[]>(recurringBreaks);
  const [isEditing, setIsEditing] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [workHoursSnapshot, setWorkHoursSnapshot] = useState<BitStringData | null>(null);
  const [breakSnapshot, setBreakSnapshot] = useState<RecurringBreak[] | null>(null);

  useEffect(() => {
    setWorkHours(initialWorkHours);
  }, [initialWorkHours]);

  useEffect(() => {
    setLocalBreaks(recurringBreaks);
  }, [recurringBreaks]);

  const hasBreak = localBreaks.length > 0;

  const calculateMiddleOfShift = (start: Dayjs, end: Dayjs): Dayjs => {
    const middleTimestamp = start.valueOf() + (end.valueOf() - start.valueOf()) / 2;
    const middle = dayjs(middleTimestamp);
    return roundToNearestInterval(middle);
  };

  const emitScheduleChange = (updated: BitStringData) => {
    setWorkHours(updated);
    onScheduleChange(day, updated);
  };

  const emitBreaksChange = (next: RecurringBreak[]) => {
    const normalized = next.map((definition) => ({
      ...definition,
      weekday: weekdayIndex,
    }));
    setLocalBreaks(normalized);
    onRecurringBreaksChange(day, normalized);
  };

  const createDefaultBreak = (): RecurringBreak => {
    const middleTime = calculateMiddleOfShift(workHours.startTime, workHours.endTime);
    const defaultStart = middleTime.subtract(30, 'minute');
    const defaultEnd = middleTime.add(30, 'minute');
    return {
      id: nanoid(),
      weekday: weekdayIndex,
      startTime: defaultStart.format('HHmm'),
      endTime: defaultEnd.format('HHmm'),
      repeatEveryXWeeks: 1,
      repeatUntilDate: formatDateToISO(templateEndDate),
      indefinite: false,
    };
  };

  const handleTimeChange = (field: 'startTime' | 'endTime', newTime: Dayjs) => {
    const updated = { ...workHours, [field]: newTime };

    if (field === 'startTime' && newTime.isAfter(workHours.endTime)) {
      alert('Start time must be before end time.');
      return;
    }

    if (field === 'endTime' && newTime.isBefore(workHours.startTime)) {
      alert('End time must be after start time.');
      return;
    }

    if (!updated.isDayOff) {
      updated.bitString = generateScheduleBitString(updated);
    }

    emitScheduleChange(updated);
  };

  const handleDayOffToggle = (checked: boolean) => {
    const updated: BitStringData = checked
      ? {
        ...workHours,
        isDayOff: true,
        bitString: generateAllOnesBitString(),
      }
      : {
        ...workHours,
        isDayOff: false,
        bitString: generateScheduleBitString({ ...workHours, isDayOff: false }),
      };
    emitScheduleChange(updated);
    if (checked) {
      // Clearing breaks prevents recurring blockers from being generated on a day off.
      emitBreaksChange([]);
    }
  };

  const handleBreakToggle = (checked: boolean) => {
    if (checked) {
      if (localBreaks.length === 0) {
        emitBreaksChange([createDefaultBreak()]);
      }
    } else {
      emitBreaksChange([]);
    }
  };

  const handleBreakTimeChange = (
    breakId: string,
    field: 'startTime' | 'endTime',
    value: Dayjs | null
  ) => {
    if (!value) return;
    const updated = localBreaks.map((definition) =>
      definition.id === breakId
        ? {
          ...definition,
          [field]: value.format('HHmm'),
        }
        : definition
    );
    emitBreaksChange(updated);
  };

  const handleRepeatIntervalChange = (breakId: string, value: number) => {
    const normalized = Math.min(4, Math.max(1, value));
    emitBreaksChange(
      localBreaks.map((definition) =>
        definition.id === breakId
          ? { ...definition, repeatEveryXWeeks: normalized }
          : definition
      )
    );
  };

  const handleRemoveBreak = (breakId: string) => {
    emitBreaksChange(localBreaks.filter((definition) => definition.id !== breakId));
  };

  const handleAddBreak = () => {
    emitBreaksChange([...localBreaks, createDefaultBreak()]);
  };

  const toggleEdit = () => {
    if (isEditing) {
      setOpenConfirmationDialog(true);
    } else {
      setWorkHoursSnapshot({ ...workHours });
      setBreakSnapshot(localBreaks.map((definition) => ({ ...definition })));
      setIsEditing(true);
    }
  };

  const capitalizeFirstLetter = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

  const breakSummary = useMemo(() => {
    if (!hasBreak) return 'No break';
    if (localBreaks.length === 1) {
      const blocker = localBreaks[0];
      return `${toDayjsFromHHmm(blocker.startTime).format('h:mm a')} - ${toDayjsFromHHmm(blocker.endTime).format('h:mm a')}`;
    }
    return `${localBreaks.length} breaks`;
  }, [hasBreak, localBreaks]);

  return (
    <Card sx={{ mb: 2, p: 1, borderRadius: '12px', boxShadow: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>
            {day}
          </Typography>
          <IconButton onClick={toggleEdit}>
            {isEditing ? <Cancel /> : <Edit />}
          </IconButton>
        </Box>

        <Collapse in={!isEditing}>
          <Box display="flex" justifyContent="space-between" flexDirection={'column'} marginTop={2}>
            {workHours.isDayOff ? (
              <Typography style={{ fontStyle: 'italic', fontWeight: 'bold' }}>Not a work day</Typography>
            ) : (
              <>
                <Typography>
                  <span style={{ fontWeight: 'bold' }}>Hours:</span> {workHours.startTime?.format('h:mm a') || 'N/A'} - {workHours.endTime?.format('h:mm a') || 'N/A'}
                </Typography>
                <Typography>
                  <span style={{ fontWeight: 'bold' }}>Breaks:</span> {breakSummary}
                </Typography>
              </>
            )}
          </Box>
        </Collapse>

        <Collapse in={isEditing}>
          <Box mt={2} display="flex" flexDirection="column" gap={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={workHours.isDayOff}
                  onChange={(e) => handleDayOffToggle(e.target.checked)}
                />
              }
              label="Day Off"
            />

            <Collapse in={!workHours.isDayOff}>
              <Box display="flex" flexDirection="column" gap={2}>
                <DesktopTimePicker
                  label="Start Time"
                  value={workHours.startTime}
                  onChange={(newTime) => newTime && handleTimeChange('startTime', newTime)}
                  minutesStep={TIME_INTERVAL}
                />
                <DesktopTimePicker
                  label="End Time"
                  value={workHours.endTime}
                  onChange={(newTime) => newTime && handleTimeChange('endTime', newTime)}
                  minutesStep={TIME_INTERVAL}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasBreak}
                      onChange={(e) => handleBreakToggle(e.target.checked)}
                    />
                  }
                  label="Has Break?"
                />
              </Box>

              <Collapse in={hasBreak}>
                <Stack spacing={2} sx={{ mt: 2 }}>
                  {localBreaks.map((blocker) => (
                    <Box key={blocker.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <DesktopTimePicker
                          label="Start"
                          value={toDayjsFromHHmm(blocker.startTime)}
                          onChange={(newTime) => handleBreakTimeChange(blocker.id, 'startTime', newTime)}
                          minutesStep={TIME_INTERVAL}
                        />
                        <DesktopTimePicker
                          label="End"
                          value={toDayjsFromHHmm(blocker.endTime)}
                          onChange={(newTime) => handleBreakTimeChange(blocker.id, 'endTime', newTime)}
                          minutesStep={TIME_INTERVAL}
                        />
                      </Stack>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={2} alignItems="center">
                        <TextField
                          select
                          label="Repeat every"
                          value={blocker.repeatEveryXWeeks}
                          onChange={(event) => handleRepeatIntervalChange(blocker.id, Number(event.target.value))}
                          sx={{ minWidth: 160 }}
                        >
                          {[1, 2, 3, 4].map((interval) => (
                            <MenuItem key={interval} value={interval}>
                              {interval} week{interval > 1 ? 's' : ''}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={1} alignItems="center">
                        <Button
                          color="error"
                          onClick={() => handleRemoveBreak(blocker.id)}
                        >
                          Remove
                        </Button>
                      </Stack>
                    </Box>
                  ))}
                  {/* For now lets not allow multiple breaks in a day */}
                  {/* <Button variant="outlined" onClick={handleAddBreak}>
                    Add Another Break
                  </Button> */}
                </Stack>
              </Collapse>
            </Collapse>
          </Box>
        </Collapse>
      </CardContent>
      <ConfirmationDialog
        open={openConfirmationDialog}
        title="Disregard Changes?"
        content=""
        onConfirm={() => {
          emitScheduleChange(workHoursSnapshot ?? initialWorkHours);
          emitBreaksChange(breakSnapshot ?? recurringBreaks);
          setWorkHoursSnapshot(null);
          setBreakSnapshot(null);
          setIsEditing(false);
          setOpenConfirmationDialog(false);
        }}
        onClose={() => setOpenConfirmationDialog(false)}
        message={`This will reset any changes made to ${capitalizeFirstLetter(day)}'s work hours.`}
      />
    </Card>
  );
};

export default ScheduleCard;
